import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  useMediaQuery,
  Stack,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import { OVERLAY_NAMES } from "constants/Overlays";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import FileCopyRoundedIcon from "@mui/icons-material/FileCopyRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import TextsmsRoundedIcon from "@mui/icons-material/TextsmsRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {
  formatSelectedMonths,
  getDateProximityColor,
  getStatusColor,
  normalizePhoneNumber,
  numericComparator,
} from "helpers/ServiceHelpers";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import CustomTab from "components/CustomTab";
import api from "services/api";
import {
  DataGrid,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import ColumnFilter from "components/ColumnFilter";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { CONST } from "constants/CONST";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import ClearIcon from "@mui/icons-material/ClearRounded";
import ColumnDateFilter from "components/ColumnDateFilter";
import TaskItem from "components/TaskItem";
import { TaskBoard, buildTaskQueryParams } from "../../workspace/tasks/Tasks";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { useAuth } from "contexts/AuthContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import { usePageTitle } from "hooks/usePageTitle";
import { HtmlTooltip } from "components/HeatMap";
import { NAVBAR_HEIGHT } from "constants/layout";
import History from "../../services/History";

const normalizeRequirementValue = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const normalizeLinguistRequirements = (linguist) => {
  if (!linguist) return linguist;

  const requirementMap = { ...(linguist.RequirementMap || {}) };
  const requirementValues = linguist.Requirements || linguist.RequirementValues || [];

  requirementValues.forEach((requirement) => {
    const sourceColumn = requirement?.Definition?.SourceColumn;
    if (sourceColumn) {
      requirementMap[sourceColumn] = normalizeRequirementValue(requirement.Value);
    }
  });

  const normalized = {
    ...linguist,
    RequirementMap: requirementMap,
  };

  Object.entries(requirementMap).forEach(([field, fieldValue]) => {
    normalized[field] = normalizeRequirementValue(fieldValue);
  });

  return normalized;
};

const LinguistProfile = () => {
  const [pageTitle, setPageTitle] = useState("Linguist");
  usePageTitle(pageTitle);

  const [tabValue, setTabValue] = React.useState(0);

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasksForService, setTasksForService] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Inline task creation state
  const [creatingTaskForStatus, setCreatingTaskForStatus] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for Linguist profile to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to specific linguist so socket joins the entity room for profile-level updates
    setCurrentEntity({
      entityType: "Linguist",
      entityId: id,
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  const { refreshPage } = useStateContext();
  const { setOverlayName, setOverlayData, showSnackbar, handleErrorDialog } =
    useStateContext();

  const theme = useTheme();

  const [linguistData, setLinguistData] = useState({});
  const [allRatesEqual, setAllRatesEqual] = useState(false);
  const [allModesEqual, setAllModesEqual] = useState(false);

  const [listofLanguages, setListOfLanguages] = useState("");
  const [listofLinguistStatuses, setListOfLinguistStatuses] = useState("");
  const [listofLanguageModes, setListofLanguageModes] = useState("");
  const [requirementDefinitions, setRequirementDefinitions] = useState([]);

  const [listofProjects, setListOfProjects] = useState([]);
  const [linguistHistories, setLinguistHistories] = useState([]);
  const [minMaxDates, setMinMaxDates] = useState();

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Define columns for filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (_, params) => params.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, params) =>
          params.Date ? dayjs.tz(params.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (_, params) => params.Client?.Account || "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (_, params) => params.Requestor?.Name || "N/A",
      },
      {
        field: "InterpretationStatus",
        valueGetter: (_, params) =>
          params.InterpretationStatus ||
          params.TranslationStatus ||
          params.OtherServiceStatus ||
          "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (_, params) => {
          const data = params.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "Details",
        valueGetter: (_, params) => params.Details || "N/A",
      },
      {
        field: "Linguist",
        valueGetter: (_, params) => params.Linguist?.Name || "N/A",
      },
      {
        field: "ServiceCategory",
        valueGetter: (_, params) => params.ServiceCategory || "N/A",
      },
      {
        field: "TotalCost",
        valueGetter: (_, params) => {
          const data = params.TotalCost;
          return data !== undefined && data !== null
            ? parseFloat(data) === 0
              ? "N/A"
              : `$${data}`
            : "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (_, params) => {
          const data = params.AdditionalCost;
          return data !== undefined && data !== null
            ? parseFloat(data) === 0
              ? "N/A"
              : `$${data}`
            : "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (_, params) => {
          const data = params.LinguistPay;
          return data !== undefined && data !== null
            ? parseFloat(data) === 0
              ? "N/A"
              : `$${data}`
            : "N/A";
        },
      },
      {
        field: "Profit",
        valueGetter: (_, params) => {
          const data = params.Profit;
          return data !== undefined && data !== null
            ? parseFloat(data) === 0
              ? "N/A"
              : `$${data}`
            : "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (_, params) => {
          const data = params.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "Language",
        valueGetter: (_, params) => params.Language?.Name || "N/A",
      },
      {
        field: "TargetLanguage",
        valueGetter: (_, params) => {
          const languages = params.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) => item.Language?.Name)
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (_, params) => params.Creator?.Name || "N/A",
      },
      {
        field: "CheckedBy",
        valueGetter: (_, params) => params.Checker?.Name || "N/A",
      },
      {
        field: "CheckedOn",
        valueGetter: (_, params) =>
          params.CheckingDate
            ? dayjs.tz(params.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
    ],
    [],
  );

  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
    setColumnFilters: setRawColumnFilters,
  } = useColumnFilters(listofProjects || [], filterColumns);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "LINGUIST_PROFILE_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `LINGUIST_PROFILE_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "InterpretationStatus",
      "AccountCharge",
      "Details",
      "Linguist",
      "ServiceCategory",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Profit",
      "Margin",
      "Language",
      "TargetLanguage",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "InterpretationStatus",
      "AccountCharge",
      "Details",
      "Linguist",
      "ServiceCategory",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Profit",
      "Margin",
      "Language",
      "TargetLanguage",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = (field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  };

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = () => {
    clearAllFilters();
    clearAllColumnFilters();
  };

  const setColumnFilters = (filterFn) => {
    const newFilters =
      typeof filterFn === "function" ? filterFn(columnFilters) : filterFn;
    Object.entries(newFilters).forEach(([field, values]) => {
      handleColumnFilterChange(field, values);
    });
  };

  // Cross-filter data: each filter shows options available in data filtered by all OTHER filters
  const filterData = useMemo(() => {
    const data = {};
    const fieldNames = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "InterpretationStatus",
      "AccountCharge",
      "Details",
      "Linguist",
      "ServiceCategory",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Profit",
      "Margin",
      "Language",
      "TargetLanguage",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fieldNames.forEach((fieldName) => {
      // Create filters excluding the current field
      const otherFilters = { ...columnFilters };
      delete otherFilters[fieldName];

      if (Object.keys(otherFilters).length === 0) {
        // If no other filters, return all data
        data[fieldName] = listofProjects || [];
      } else {
        // Apply all other filters to show only available options
        data[fieldName] = (listofProjects || []).filter((row) => {
          return Object.entries(otherFilters).every(
            ([field, selectedValues]) => {
              if (!selectedValues || selectedValues.length === 0) return true;

              const column = filterColumns.find((col) => col.field === field);
              const cellValue = column?.valueGetter
                ? column.valueGetter(null, row)
                : row[field] || "N/A";

              return selectedValues.includes(cellValue);
            },
          );
        });
      }
    });

    return data;
  }, [columnFilters, listofProjects, filterColumns]);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Year selector data
  const currentYear = dayjs().year();

  const [yearOptions, setYearOptions] = useState([currentYear]);

  const [selectedYear, setSelectedYear] = useState([String(currentYear)]);
  const [tempSelectedYear, setTempSelectedYear] = useState([
    String(currentYear),
  ]);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
        }
      }
    }
  }, [currentYear, minMaxDates, selectedYear]);

  useEffect(() => {
    const fetchTaskEnums = async () => {
      try {
        const [statusesRes] = await Promise.all([
          api.get("/tasks/enums/statuses"),
        ]);
        setTaskStatuses(statusesRes.data || []);
      } catch (error) {
        console.error("Error fetching task enums:", error);
      }
    };

    fetchTaskEnums();
  }, []);

  useEffect(() => {
    const fetchTasksForService = async () => {
      if (!id) return;
      setTasksLoading(true);
      try {
        const params = buildTaskQueryParams(
          {
            entityServiceTypes: ["Linguist"],
            showAllCompleted: true,
          },
          currentUser?.id,
        );
        params.append("entityId", id);

        const response = await api.get(`/tasks?${params.toString()}`);
        setTasksForService(response.data || []);
      } catch (error) {
        console.error("Error fetching tasks for service:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        setTasksLoading(false);
      }
    };

    fetchTasksForService();
  }, [currentUser?.id, handleErrorDialog, id, refreshPage, theme]);

  // Listen for task updates linked to this linguist
  useEffect(() => {
    if (!socket || !id) return;

    const handleTaskUpdate = async (data) => {
      // Check if task is linked to this linguist
      if (data.entityType === "Linguist" && data.entityID === parseInt(id)) {
        // Refetch tasks for this linguist
        try {
          const params = buildTaskQueryParams(
            {
              entityServiceTypes: ["Linguist"],
              showAllCompleted: true,
            },
            currentUser?.id,
          );
          params.append("entityId", id);

          const response = await api.get(`/tasks?${params.toString()}`);
          setTasksForService(response.data || []);

          // Show snackbar notification
          const userName = data.updatedBy?.userName || "Unknown User";
          const message = `Task ${data.changeAction} by ${userName}`;
          showSnackbar(message, theme, "info");
        } catch (error) {
          console.error("Error refreshing tasks:", error);
        }
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, id, currentUser?.id, showSnackbar, theme]);

  const currentMonth = dayjs().format("MMM");
  const [selectedMonths, setSelectedMonths] = useState([String(currentMonth)]);
  const [tempSelectedMonths, setTempSelectedMonths] = useState([
    String(currentMonth),
  ]);

  // Handle date selection save (months and years combined)
  const handleDateSelectionSave = (newSelection) => {
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Single year
    };
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);
  };

  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState([]);

  const requirementDetailItems = useMemo(() => {
    const normalizeRequirementIdentifier = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

    const isTmioRequirement = (definition) => {
      const sourceColumn = normalizeRequirementIdentifier(definition?.SourceColumn);
      const key = normalizeRequirementIdentifier(definition?.Key);
      const name = normalizeRequirementIdentifier(definition?.Name);

      return (
        sourceColumn === "tmio" ||
        sourceColumn === "themedicalinterpreteronline" ||
        key === "tmio" ||
        key === "themedicalinterpreteronline" ||
        name === "tmio" ||
        name === "themedicalinterpreteronline"
      );
    };

    const definitions = requirementDefinitions.filter(
      (definition) => definition?.SourceColumn,
    );

    return definitions.map((definition) => {
      const sourceColumn = definition.SourceColumn;
      const mapValue = normalizeRequirementValue(
        linguistData?.RequirementMap?.[sourceColumn],
      );
      const directValue = normalizeRequirementValue(linguistData?.[sourceColumn]);
      const resolvedValue = mapValue !== null ? mapValue : directValue;
      const tmioRequirement = isTmioRequirement(definition);
      const value = tmioRequirement
        ? resolvedValue === true
        : resolvedValue;

      return {
        key: sourceColumn,
        label: definition.Name || sourceColumn,
        value,
        isTmioRequirement: tmioRequirement,
      };
    });
  }, [linguistData, requirementDefinitions]);

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    taskStatuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    taskStatuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!taskStatuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleTaskDragEnd = async (result) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    const taskId = draggableId.replace("task-", "");

    setTasksForService((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasksForService.find(
          (t) => t.id.toString() === taskId,
        );
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      const params = buildTaskQueryParams(
        {
          entityServiceTypes: ["Interpretation"],
          showAllCompleted: true,
        },
        currentUser?.id,
      );
      params.append("entityId", id);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasksForService(response.data || []);
    }
  };

  // Handle inline task creation
  const handleCreateInlineTask = async (status) => {
    if (!newTaskTitle.trim()) return;

    try {
      const response = await api.post("/tasks/create", {
        Title: newTaskTitle,
        Status: status,
        Priority: "Normal",
        CreatedByUserID: currentUser.id,
        AssignedToUserID: currentUser.id,
        EntityType: "Linguist",
        EntityID: id,
      });

      setNewTaskTitle("");
      setCreatingTaskForStatus(null);

      const createdTask = response.data;
      setTasksForService((prevTasks) => {
        const tasksByStatus = groupTasksByStatus(prevTasks);
        const statusTasks = tasksByStatus[status] || [];
        tasksByStatus[status] = [...statusTasks, createdTask];
        return flattenTasksByStatus(tasksByStatus);
      });
    } catch (error) {
      console.error("Error creating task:", error);
      handleErrorDialog("Failed to create task", theme);
    }
  };

  const renderTaskCard = (task, index) => (
    <Draggable
      draggableId={`task-${task.id}`}
      index={index}
      key={task.id}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          task={task}
          onClick={() => {
            setOverlayData({ Task: task });
            setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
          }}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  );

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  columnFilteredData &&
    columnFilteredData.forEach((service) => {
      const include = includeAll || filteredRowIdsSet.has(String(service.id));
      if (!include) return;

      totalAccountCharge += Number(service.AccountCharge);
      totalTotalCost += Number(service.TotalCost);
      totalProfit += Number(service.Profit);
    });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let confirmedCount = 0;
  let requestedCount = 0;
  let cancelledCount = 0;

  columnFilteredData &&
    columnFilteredData.forEach((service) => {
      const include = includeAll || filteredRowIdsSet.has(String(service.id));
      if (!include) return;

      const status =
        service.InterpretationStatus ||
        service.TranslationStatus ||
        service.OtherServiceStatus ||
        null;
      if (status === "Completed") completedCount++;
      else if (status === "Confirmed") confirmedCount++;
      else if (status === "Requested") requestedCount++;
      else if (status === "Cancelled" || service.Cancelled) cancelledCount++;
    });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Confirmed", count: confirmedCount },
    { name: "Requested", count: requestedCount },
    { name: "Cancelled", count: cancelledCount },
  ];

  // Toggle between totals and statuses (default Admin: totals, Member: statuses)
  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.LINGUIST_PROFILE_SHOW_TOTALS_KEY);
    if (saved !== null) return JSON.parse(saved);
    return currentUser?.role !== "Admin";
  };
  const [showTotals, setShowTotals] = useState(getInitialShowTotals);
  const handleToggleShowTotals = () => {
    const next = !showTotals;
    setShowTotals(next);
    localStorage.setItem(
      CONST.LINGUIST_PROFILE_SHOW_TOTALS_KEY,
      JSON.stringify(next),
    );
  };

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexWrap: "wrap", // wrap to new line if too long
            gap: 2, // spacing between items
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleToggleShowTotals}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              "&:hover": { color: theme.palette.primary.buttonHoverSelected },
            }}
          >
            <SwapHorizRoundedIcon fontSize="small" />
          </IconButton>

          {(showTotals ? listOfTotals : listOfServiceStatuses).map((item) => {
            return (
              <Box key={`${item}-${item.name}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.name}:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.secondary?.[400],
                      fontWeight: 600,
                    }}
                  >
                    {showTotals
                      ? `$${Number(item.total).toLocaleString()}`
                      : item.count}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  const fetchProjectsData = useCallback((months = selectedMonths, year = selectedYear) => {
    setIsLoading(true);

    // Ensure we have valid parameters
    if (!months || months.length === 0) {
      months = [dayjs().format("MMM")];
      // Update both states to reflect the default value
      setSelectedMonths(months);
    }

    if (!year || year.length === 0) {
      year = [String(currentYear)];
      // Update both states to reflect the default value
      setSelectedYear(year);
    }

    // Convert months to a comma-separated string if it's an array
    const monthsParam = Array.isArray(months) ? months.join(",") : months;

    api
      .get(`/services/bylinguist/${id}`, {
        params: {
          months: monthsParam,
          year: year[0], // Since year is single-select, we only need the first (and only) value
        },
      })
      .then((response) => {
        if (response !== null) {
          //REVERSE LISTS
          const services = response.data;
          services.reverse();
          setListOfProjects(services);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [currentYear, handleErrorDialog, id, selectedMonths, selectedYear, theme]);

  useEffect(() => {
    const fetchLinguistProfileData = () => {
      api.get(`/linguists/${id}`).then((response) => {
        if (response !== null) {
          fetchProjectsData();

          const linguist = normalizeLinguistRequirements(response.data);

          setLinguistData(linguist);

          setPageTitle("Linguist: " + linguist.Name);

          setAllRatesEqual(
            linguist.LinguistLanguages.every(
              (item, _, arr) => item.Rate === arr[0].Rate,
            ),
          );
          setAllModesEqual(
            linguist.LinguistLanguages.every(
              (item, _, arr) => item.Mode === arr[0].Mode,
            ),
          );

          api.get("/languages").then((response) => {
            if (response !== null) setListOfLanguages(response.data);
          });

          api.get("/linguists/linguistStatuses").then((response) => {
            if (response !== null) setListOfLinguistStatuses(response.data);
          });

          api.get("/linguists/languageModes").then((response) => {
            if (response !== null) setListofLanguageModes(response.data);
          });

          api.get("/linguists/requirements").then((response) => {
            if (response !== null) {
              setRequirementDefinitions(response.data || []);
            }
          });

          api.get("/dashboard/serviceDateRange").then((response) => {
            if (response !== null) setMinMaxDates(response.data);
          });

          api.get(`/auditLogs/entity/Linguist/${id}`).then((response) => {
            if (response !== null) {
              setLinguistHistories(response.data?.auditLogs || []);
            }
          });
        }
      });
    };

    fetchLinguistProfileData();
  }, [fetchProjectsData, id, refreshPage]);

  // Listen for linguist updates from other members
  const handleLinguistProfileRefetch = useCallback(
    (updatedBy) => {
      // Store update info for snackbar (separate effect will show it)
      setLastUpdate(updatedBy);

      // Refetch the linguist data
      api.get(`/linguists/${id}`).then((response) => {
        if (response !== null) {
          const linguist = normalizeLinguistRequirements(response.data);
          setLinguistData(linguist);
          setPageTitle("Linguist: " + linguist.Name);

          setAllRatesEqual(
            linguist.LinguistLanguages.every(
              (item, _, arr) => item.Rate === arr[0].Rate,
            ),
          );
          setAllModesEqual(
            linguist.LinguistLanguages.every(
              (item, _, arr) => item.Mode === arr[0].Mode,
            ),
          );

          api.get(`/auditLogs/entity/Linguist/${id}`).then((auditResponse) => {
            if (auditResponse !== null) {
              setLinguistHistories(auditResponse.data?.auditLogs || []);
            }
          });
        }
      });
    },
    [id],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Linguist updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "Linguist",
    recordId: id,
    onUpdate: handleLinguistProfileRefetch,
  });

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (listofProjects && listofProjects.length > 0) {
      const savedFilters = loadAllColumnFilters();
      setRawColumnFilters(savedFilters);
    }
  }, [listofProjects, loadAllColumnFilters, setRawColumnFilters]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return (
      dayjs.tz(v1, "M/D/YYYY").valueOf() - dayjs.tz(v2, "M/D/YYYY").valueOf()
    );
  };

  const getColumns = () => [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 120,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Number"
          children={
            <ColumnFilter
              field="ProjectNumber"
              data={filterData["ProjectNumber"] || []}
              selectedValues={columnFilters["ProjectNumber"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("ProjectNumber", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "ProjectNumber")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDateProximityColor(theme, params.value),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader
          headerName="Date"
          children={
            <ColumnDateFilter
              field="Date"
              data={filterData["Date"] || []}
              selectedValues={columnFilters["Date"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Date", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Date")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Client"
          children={
            <ColumnFilter
              field="Client"
              data={filterData["Client"] || []}
              valueGetter={(_, params) => {
                const data = params.Client;
                const value = data ? data.Account : "N/A";
                return value;
              }}
              selectedValues={columnFilters["Client"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Client", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Client")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Requestor"
          children={
            <ColumnFilter
              field="Requestor"
              data={filterData["Requestor"] || []}
              valueGetter={(_, params) => {
                const data = params.Requestor;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["Requestor"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Requestor", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Requestor")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      valueGetter: (_, params) => {
        const value =
          params.InterpretationStatus ||
          params.TranslationStatus ||
          params.OtherServiceStatus ||
          "N/A";
        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader
          headerName="Status"
          children={
            <ColumnFilter
              field="InterpretationStatus"
              data={filterData["InterpretationStatus"] || []}
              valueGetter={(_, params) => {
                const value =
                  params.InterpretationStatus ||
                  params.TranslationStatus ||
                  params.OtherServiceStatus ||
                  "N/A";
                return value;
              }}
              selectedValues={columnFilters["InterpretationStatus"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  InterpretationStatus: values,
                }))
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "InterpretationStatus")
                  ?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: () => (
        <DataGridHeader
          headerName="Account Charge"
          children={
            <ColumnFilter
              field="AccountCharge"
              data={filterData["AccountCharge"] || []}
              selectedValues={columnFilters["AccountCharge"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("AccountCharge", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "AccountCharge")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Details"
          children={
            <ColumnFilter
              field="Details"
              data={filterData["Details"] || []}
              selectedValues={columnFilters["Details"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Details", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Details")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist"
          children={
            <ColumnFilter
              field="Linguist"
              data={filterData["Linguist"] || []}
              valueGetter={(_, params) => {
                const data = params.Linguist;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["Linguist"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Linguist", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Linguist")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "ServiceCategory",
      headerName: "Category",
      valueGetter: (_, params) => {
        const data = params.ServiceCategory;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Category"
          children={
            <ColumnFilter
              field="ServiceCategory"
              data={filterData["ServiceCategory"] || []}
              selectedValues={columnFilters["ServiceCategory"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  ServiceCategory: values,
                }))
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "ServiceCategory")?.sort ||
                null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Total Cost"
          children={
            <ColumnFilter
              field="TotalCost"
              data={filterData["TotalCost"] || []}
              selectedValues={columnFilters["TotalCost"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("TotalCost", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "TotalCost")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: () => (
        <DataGridHeader
          headerName="Additional Cost"
          children={
            <ColumnFilter
              field="AdditionalCost"
              data={filterData["AdditionalCost"] || []}
              selectedValues={columnFilters["AdditionalCost"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  AdditionalCost: values,
                }))
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "AdditionalCost")?.sort ||
                null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist Pay"
          children={
            <ColumnFilter
              field="LinguistPay"
              data={filterData["LinguistPay"] || []}
              selectedValues={columnFilters["LinguistPay"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("LinguistPay", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "LinguistPay")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: () => (
        <DataGridHeader
          headerName="Profit"
          children={
            <ColumnFilter
              field="Profit"
              data={filterData["Profit"] || []}
              selectedValues={columnFilters["Profit"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Profit", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Profit")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 105,
      flex: 1.05,
      renderHeader: () => (
        <DataGridHeader
          headerName="Margin"
          children={
            <ColumnFilter
              field="Margin"
              data={filterData["Margin"] || []}
              selectedValues={columnFilters["Margin"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Margin", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Margin")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Language",
      headerName: "Source Language",
      valueGetter: (_, params) => {
        const data = params.Language;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 175,
      flex: 1.75,
      renderHeader: () => (
        <DataGridHeader
          headerName="Source Language"
          children={
            <ColumnFilter
              field="Language"
              data={filterData["Language"] || []}
              valueGetter={(_, params) => {
                const data = params.Language;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["Language"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Language", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Language")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Target Languages",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 300,
      flex: 3.0,
      renderHeader: () => (
        <DataGridHeader
          headerName="Target Languages"
          children={
            <ColumnFilter
              field="TargetLanguage"
              data={filterData["TargetLanguage"] || []}
              valueGetter={(_, params) => {
                const languages = params.ServiceTargetLanguages;
                if (!languages || languages.length === 0) return "N/A";
                return languages
                  .map((item) =>
                    item.Language && item.Language.Name
                      ? item.Language.Name
                      : "",
                  )
                  .filter((name) => name)
                  .join(" | ");
              }}
              selectedValues={columnFilters["TargetLanguage"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  TargetLanguage: values,
                }))
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "TargetLanguage")?.sort ||
                null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Created By"
          children={
            <ColumnFilter
              field="CreatedBy"
              data={filterData["CreatedBy"] || []}
              valueGetter={(_, params) => {
                const data = params.Creator;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["CreatedBy"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CreatedBy", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CreatedBy")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: () => (
        <DataGridHeader
          headerName="Checked By"
          children={
            <ColumnFilter
              field="CheckedBy"
              data={filterData["CheckedBy"] || []}
              valueGetter={(_, params) => {
                const data = params.Checker;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["CheckedBy"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CheckedBy", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CheckedBy")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 135,
      flex: 1.35,
      renderHeader: () => (
        <DataGridHeader
          headerName="Checked On"
          children={
            <ColumnDateFilter
              field="CheckedOn"
              data={filterData["CheckedOn"] || []}
              valueGetter={(_, params) => {
                const data = params.CheckingDate;
                const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
                return value;
              }}
              selectedValues={columnFilters["CheckedOn"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CheckedOn", values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CheckedOn")?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
  ];

  const navigate = useNavigate();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = !useMediaQuery("(min-width:600px)");

  const accountInformation = [
    {
      id: 0,
      key: "0-" + linguistData.Email,
      Title: "Email",
      Value: linguistData.Email,
    },
    {
      id: 1,
      key: "1-" + linguistData.Address,
      Title: "Address",
      Value: linguistData.Address,
    },
    {
      id: 2,
      key: "2-" + linguistData.PhoneNumber,
      Title: "PhoneNumber",
      Value: normalizePhoneNumber(linguistData.PhoneNumber),
    },
    {
      id: 3,
      key: "3-" + linguistData.Active,
      Title: "Status",
      Value: linguistData.Active ? "Active" : "Inactive",
    },
    {
      id: 4,
      key: "4-" + linguistData.Certifications,
      Title: "Certifications",
      Value: linguistData.Certifications,
    },
    {
      id: 5,
      key: "5-" + linguistData.Notes,
      Title: "Notes",
      Value: linguistData.Notes,
    },
  ];

  const copyToClipboard = useCallback(
    async (value, label) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(String(value));
        showSnackbar(`${label} copied`, theme, "success");
      } catch {
        showSnackbar(`Failed to copy ${label.toLowerCase()}`, theme, "error");
      }
    },
    [showSnackbar, theme],
  );

  const handleOpenDocumentGenerationOverlay = useCallback(() => {
    if (!id) return;

    setOverlayData({
      LinguistID: Number(id),
      Linguist: linguistData,
    });
    setOverlayName(OVERLAY_NAMES.LINGUIST_DOCUMENT_GENERATION);
  }, [id, linguistData, setOverlayData, setOverlayName]);

  const handleOpenGeneratedDocumentsOverlay = useCallback(() => {
    if (!id) return;

    setOverlayData({
      LinguistID: Number(id),
      Linguist: linguistData,
    });
    setOverlayName(OVERLAY_NAMES.LINGUISTS_GENERATED_DOCUMENTS);
  }, [id, linguistData, setOverlayData, setOverlayName]);

  const handleOpenEditOverlay = useCallback(() => {
    const secondData = {
      LinguistID: id,
      Linguist: linguistData,
      Languages: listofLanguages,
      LinguistStatuses: listofLinguistStatuses,
      LanguageModes: listofLanguageModes,
      Action: OVERLAY_NAMES.LINGUIST_ACTION_EDIT,
    };

    setOverlayData(secondData);
    setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);
  }, [
    id,
    linguistData,
    listofLanguages,
    listofLinguistStatuses,
    listofLanguageModes,
    setOverlayData,
    setOverlayName,
  ]);

  const renderAccountInfoActions = useCallback(
    (item) => {
      if (!item?.Value) return null;

      const actionIconButtonSx = {
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: "8px",
        p: 0.25,
        width: 24,
        height: 24,
        "& .MuiSvgIcon-root": {
          fontSize: 16,
        },
        "&:hover": {
          borderColor: theme.palette.primary.buttonHoverSelected,
          backgroundColor: theme.palette.primary.buttonHoverSelected,
          color: theme.palette.background.default,
        },
      };

      if (item.Title === "Address") {
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          item.Value,
        )}`;
        return (
          <Stack direction="row" spacing={0.75}>
            <IconButton
              component="a"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Open in Google Maps"
              sx={actionIconButtonSx}
            >
              <MapRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Copy address"
              onClick={() => copyToClipboard(item.Value, "Address")}
              sx={actionIconButtonSx}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      }

      if (item.Title === "PhoneNumber") {
        const rawPhone = String(linguistData.PhoneNumber || "").trim();
        const callPhone = rawPhone.replace(/[^0-9+]/g, "");
        return (
          <Stack direction="row" spacing={0.75}>
            <IconButton
              component="a"
              href={callPhone ? `tel:${callPhone}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Call phone number"
              disabled={!callPhone}
              sx={actionIconButtonSx}
            >
              <CallRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              component="a"
              href={callPhone ? `sms:${callPhone}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Text phone number"
              disabled={!callPhone}
              sx={actionIconButtonSx}
            >
              <TextsmsRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Copy phone number"
              onClick={() => copyToClipboard(item.Value, "Phone number")}
              sx={actionIconButtonSx}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      }

      if (item.Title === "Email") {
        const emailValue = String(item.Value || "").trim();
        const mailtoValue = emailValue.replace(/\s+/g, "");
        return (
          <Stack direction="row" spacing={0.75}>
            <IconButton
              component="a"
              href={mailtoValue ? `mailto:${mailtoValue}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Send email"
              disabled={!mailtoValue}
              sx={actionIconButtonSx}
            >
              <EmailRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Copy email"
              onClick={() => copyToClipboard(emailValue, "Email")}
              sx={actionIconButtonSx}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      }

      return null;
    },
    [copyToClipboard, linguistData.PhoneNumber, theme],
  );

  return (
    <Box
      //borderRadius="1.5rem"
      //m=".5rem 1.5rem"
      p={isSmallScreen ? "0.5rem 0.5rem 0 0.5rem" : "1rem 1rem 0 1rem"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={0}
            sx={{ pr: 1 }}
          >
            {!isMobile ? (
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  onClick={handleOpenDocumentGenerationOverlay}
                  startIcon={<NoteAddRoundedIcon />}
                  sx={{
                    width: "220px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>New Document</Box>
                </Button>

                <Button
                  onClick={handleOpenGeneratedDocumentsOverlay}
                  startIcon={<FileCopyRoundedIcon />}
                  sx={{
                    width: "220px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>All Documents</Box>
                </Button>

                <Button
                  onClick={handleOpenEditOverlay}
                  startIcon={<EditRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Edit Linguist</Box>
                </Button>
              </Box>
            ) : (
              <Box display="flex" gap={2} alignItems="center">
                <IconButton
                  onClick={handleOpenDocumentGenerationOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.background.default,
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    border: "none",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <NoteAddRoundedIcon />
                </IconButton>

                <IconButton
                  onClick={handleOpenGeneratedDocumentsOverlay}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.background.default,
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    border: "none",
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHover,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <FileCopyRoundedIcon />
                </IconButton>

                <Button
                  onClick={handleOpenEditOverlay}
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <EditRoundedIcon />
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        id="test"
        borderRadius="12px"
        bgcolor={theme.palette.background.alt}
        sx={{
          //flex: 1,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          overflow: isSmallScreen ? "auto" : "hidden",
          height: "100%",
          pb: 1,
          px: 1,
          pt: 2,
        }}
      >
        <Grid
          container
          spacing={isSmallScreen ? 1 : 2}
          sx={{ flex: 1, width: "100%" }}
        >
          <Grid
            size={{ xs: 12, md: "auto" }}
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: isSmallScreen ? "100%" : "300px",
              maxWidth: isSmallScreen ? "100%" : "300px",
              height: isSmallScreen ? "auto" : "100%",
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                flex: 1,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* SettingsCard Component Definition */}
              {(() => {
                const SettingsCard = ({ children, title, onEdit }) => (
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.default,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      p: isSmallScreen ? "0.75rem" : "1.25rem",
                      m: "0.25rem",
                      boxShadow: theme.shadows[3],
                    }}
                  >
                    {title && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant={isSmallScreen ? "h4" : "h3"}
                            fontWeight="bold"
                          >
                            {title}
                          </Typography>
                          {onEdit && (
                            <IconButton
                              aria-label="Edit"
                              onClick={onEdit}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              <EditRoundedIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                      </>
                    )}
                    {children}
                  </Box>
                );

                return (
                  <>
                    {/* Account Information Card */}
                    <SettingsCard title="Account Information">
                      {accountInformation.every(
                        (element) => element.Value === null,
                      ) ? (
                        <Typography variant="body2">
                          No information available.
                        </Typography>
                      ) : (
                        <>
                          {accountInformation.map((item, index) =>
                            item.Value === null || item.Value === "" ? null : (
                              <Box
                                key={
                                  "AccountInfo-" +
                                  index +
                                  "-*-" +
                                  item.Value +
                                  "-" +
                                  item.Title
                                }
                                sx={{
                                  mb:
                                    index <
                                    accountInformation.filter((i) => i.Value)
                                      .length -
                                      1
                                      ? 2
                                      : 0,
                                }}
                              >
                                <Box sx={{ mb: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {item.Title === "PhoneNumber"
                                        ? "Phone Number"
                                        : item.Title}
                                    </Typography>
                                    {renderAccountInfoActions(item)}
                                  </Box>
                                  <Typography variant="body1" sx={{ mt: 0.25 }}>
                                    {item.Value}
                                  </Typography>
                                </Box>
                                {index <
                                  accountInformation.filter((i) => i.Value)
                                    .length -
                                    1 && <Divider sx={{ my: 1 }} />}
                              </Box>
                            ),
                          )}
                        </>
                      )}
                    </SettingsCard>

                    {/* Details Card */}
                    <SettingsCard title="Details">
                      <>
                        {allRatesEqual && (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Rate
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.25 }}>
                                {linguistData.LinguistLanguages[0]?.Rate ||
                                  "N/A"}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                          </>
                        )}

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Languages
                          </Typography>
                          <Box sx={{ mt: 1, ml: 2 }}>
                            {linguistData.LinguistLanguages &&
                              linguistData.LinguistLanguages.map(
                                (item, idx) => (
                                  <Typography
                                    key={
                                      item.Language.Name +
                                      "-/-" +
                                      item.Language.id +
                                      "--" +
                                      item.Mode
                                    }
                                    variant="body2"
                                    sx={{
                                      mb:
                                        idx <
                                        linguistData.LinguistLanguages.length -
                                          1
                                          ? 0.5
                                          : 0,
                                    }}
                                  >
                                    •{" "}
                                    {allRatesEqual
                                      ? item.Language.Name
                                      : allModesEqual
                                        ? item.Language.Name +
                                          " (" +
                                          item.Rate +
                                          ")"
                                        : item.Language.Name +
                                          " (" +
                                          item.Rate +
                                          " - " +
                                          item.Mode +
                                          ")"}
                                  </Typography>
                                ),
                              )}
                          </Box>
                        </Box>

                        {linguistData.LinguistLanguages &&
                        linguistData.MinimumHours !== null ? (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Minimum Hours
                              </Typography>
                              <Typography variant="body1" sx={{ mt: 0.25 }}>
                                {linguistData.MinimumHours}
                              </Typography>
                            </Box>
                          </>
                        ) : null}
                      </>
                    </SettingsCard>
                  </>
                );
              })()}
            </Box>
          </Grid>

          <Divider
            orientation={isSmallScreen ? "horizontal" : "vertical"}
            flexItem
          />

          <Grid
            size={{ xs: 12, sm: 12, md: "grow" }}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              minWidth: 0,
              flexGrow: 1,
              height: "100%",
            }}
          >
            <Box
              sx={{
                bgcolor: theme.palette.background.default,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.primary.border2}`,
                p: isSmallScreen ? "0.75rem" : "1.25rem",
                m: "0.25rem",
                boxShadow: theme.shadows[3],
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabsChange}
                  aria-label="basic tabs example"
                  variant={isSmallScreen ? "scrollable" : "standard"}
                  scrollButtons={isSmallScreen ? "auto" : false}
                  sx={{ bgcolor: theme.palette.background.default }}
                >
                  <CustomTab label="Additional Details" {...a11yProps(0)} />
                  <CustomTab label="Tasks" {...a11yProps(1)} />
                  <CustomTab label="Projects" {...a11yProps(2)} />
                  <CustomTab label="History" {...a11yProps(3)} />
                </Tabs>
              </Box>

              <DragDropContext onDragEnd={handleTaskDragEnd}>
                {/*<CustomTab label="Availability" {...a11yProps(0)} />
                <CustomTabPanel
                  value={tabValue}
                  index={0}
                  key="Tab One"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/*linguistData && linguistData.LinguistAvailabilities ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      height: "100%",
                    }}
                  >
                    <HeatMap
                      data={linguistData.LinguistAvailabilities}
                      theme={theme}
                      mode={mode}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                ) : null}
                </CustomTabPanel>*/}

                <CustomTabPanel
                  value={tabValue}
                  index={0}
                  key="Tab Additional Details"
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                    overflow: "auto",
                    py: 2,
                    px: 1,
                  }}
                >
                  <Grid container spacing={1.5}>
                    {requirementDetailItems.map((requirementItem) => {
                      const isPositive = requirementItem.value === true;

                      return (
                        <Grid key={requirementItem.key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Box
                            sx={{
                              border: `1px solid ${theme.palette.primary.border2}`,
                              borderRadius: "10px",
                              bgcolor: theme.palette.background.alt,
                              boxShadow: `
                                inset 2px 2px 5px ${alpha(theme.palette.common.black, 0.12)},
                                inset -2px -2px 5px ${alpha(theme.palette.common.white, 0.35)}
                              `,
                              px: 1,
                              py: 0.75,
                              minHeight: 54,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              transition: "box-shadow 0.2s ease-in-out",
                              "&:hover": {
                                boxShadow: `
                                  inset 3px 3px 7px ${alpha(theme.palette.common.black, 0.14)},
                                  inset -3px -3px 7px ${alpha(theme.palette.common.white, 0.4)}
                                `,
                              },
                              "&:active": {
                                boxShadow: `
                                  inset 4px 4px 9px ${alpha(theme.palette.common.black, 0.16)},
                                  inset -4px -4px 9px ${alpha(theme.palette.common.white, 0.45)}
                                `,
                              },
                            }}
                          >
                            {isPositive ? (
                              <CheckRoundedIcon
                                fontSize="small"
                                sx={{ color: theme.palette.primary.main }}
                              />
                            ) : (
                              <ClearRoundedIcon
                                fontSize="small"
                                sx={{ color: theme.palette.error.main }}
                              />
                            )}

                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                lineHeight: 1.25,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {requirementItem.label}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CustomTabPanel>
                <CustomTabPanel
                  value={tabValue}
                  index={1}
                  key="Tab Two"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "visible",
                    minHeight: 0,
                  }}
                >
                  <TaskBoard
                    statuses={taskStatuses}
                    tasks={tasksForService}
                    isLoading={tasksLoading}
                    onDragEnd={handleTaskDragEnd}
                    renderTaskCard={renderTaskCard}
                    creatingTaskForStatus={creatingTaskForStatus}
                    newTaskTitle={newTaskTitle}
                    setNewTaskTitle={setNewTaskTitle}
                    handleCreateInlineTask={handleCreateInlineTask}
                    setCreatingTaskForStatus={setCreatingTaskForStatus}
                    enableInlineCreate={true}
                    columnsOverride={2}
                    theme={theme}
                    heightFraction={1}
                    rowHeightFraction={0.5}
                    containerPadding="0.5rem"
                    containerMargin="0"
                    gridSpacing={2}
                    externalDragContext={true}
                  />

                  {/* Removed priority color code popover */}
                </CustomTabPanel>
                <CustomTabPanel
                  value={tabValue}
                  index={2}
                  key="Tab Three"
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    borderRadius="1rem"
                    bgcolor={theme.palette.background.default}
                    p="0.5rem"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      [`.${gridClasses.cell}`]: {
                        color: theme.palette.primary.text,
                      },
                      [`.${gridClasses.cell}.notAvailable`]: {
                        color: theme.palette.primary.text3,
                      },
                    }}
                  >
                    <DataGrid
                      loading={isLoading}
                      pageSizeOptions={[20, 50, 100]}
                      getRowId={(row) => row.id}
                      rows={columnFilteredData || []}
                      sortModel={sortModel}
                      columns={getColumns()}
                      onStateChange={(e) => {
                        const lookup = e.filter?.filteredRowsLookup;
                        if (!lookup) return;

                        const nextIds = Object.keys(lookup).filter(
                          (key) => lookup[key],
                        );
                        nextIds.sort();

                        const prevIds = prevFilteredRowIdsRef.current;
                        const isSame =
                          prevIds.length === nextIds.length &&
                          prevIds.every((id, idx) => id === nextIds[idx]);

                        if (!isSame) {
                          prevFilteredRowIdsRef.current = nextIds;
                          setFilterRowIds(nextIds);
                        }
                      }}
                      rowSelectionModel={openFilter ? [] : undefined}
                      disableRowSelectionOnClick={!!openFilter}
                      onRowClick={(params, event, details) => {
                        if (openFilter) {
                          setOpenFilter(null);
                          return;
                        }
                        let sendTo;

                        let data;

                        switch (params.row.ServiceCategory) {
                          case "Interpretation":
                            sendTo = "interpretations";
                            data = {
                              Interpretation: params.row,
                              InterpretationID: params.row.id,
                              ComingFrom: `/linguists/${id}`,
                            };
                            break;
                          case "Translation":
                            sendTo = "translations";
                            data = {
                              Translation: params.row,
                              TranslationID: params.row.id,
                              ComingFrom: `/linguists/${id}`,
                            };
                            break;
                          case "Other":
                            sendTo = "otherServices";
                            data = {
                              OtherService: params.row,
                              OtherServiceID: params.row.id,
                              ComingFrom: `/linguists/${id}`,
                            };
                            break;
                          default:
                            return;
                        }

                        navigate(`/${sendTo}/${params.row.id}`, {
                          state: data,
                        });
                      }}
                      getCellClassName={(params) => {
                        return params.value === "✘" ||
                          params.value === "N/A" ||
                          params.value === "0%" ||
                          params.value === "null"
                          ? "notAvailable"
                          : "";
                      }}
                      slots={{
                        noRowsOverlay: () => (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                            }}
                          >
                            <Typography variant="h4">
                              We have no assignments with {linguistData.Name} on{" "}
                              {formatSelectedMonths(selectedMonths)}{" "}
                              {selectedYear}.
                            </Typography>
                          </Box>
                        ),
                        toolbar: () => (
                          <GridToolbarContainer
                            sx={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box m="1"></Box>

                            <Box
                              sx={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                              }}
                            >
                              {hasActiveFilters && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ClearIcon />}
                                  onClick={handleClearAllFilters}
                                  sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    "&:hover": {
                                      borderColor:
                                        theme.palette.primary
                                          .buttonHoverSelected,
                                      backgroundColor:
                                        theme.palette.primary
                                          .buttonHoverSelected,
                                      color: theme.palette.background.default,
                                    },
                                  }}
                                >
                                  Clear Filters
                                </Button>
                              )}
                            </Box>

                            <Box
                              display="flex"
                              alignItems="end"
                              justifyContent="flex-end"
                              width="100%"
                              gap={2}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: FILTER_SELECTOR_WIDTH,
                                }}
                              >
                                <CustomDateSelector
                                  label="Date Range"
                                  months={months}
                                  years={
                                    yearOptions.length > 0
                                      ? yearOptions
                                      : [String(currentYear)]
                                  }
                                  selectedMonths={tempSelectedMonths}
                                  selectedYears={tempSelectedYear}
                                  onSave={handleDateSelectionSave}
                                  isMultiMonth={true}
                                  isMultiYear={false}
                                  monthGridItemSize={3}
                                  yearGridItemSize={4}
                                  showClearFilter={false}
                                />
                              </Box>
                            </Box>
                            <Box m="1"></Box>
                          </GridToolbarContainer>
                        ),
                        footer: customFooter,
                      }}
                      slotProps={{
                        row: {},
                        loadingOverlay: {
                          variant: "skeleton",
                          noRowsVariant: "skeleton",
                        },
                        toolbar: {
                          showQuickFilter: true,
                          printOptions: { disableToolbarButton: true },
                          csvOptions: { disableToolbarButton: true },
                        },
                      }}
                      ignoreDiacritics
                      sx={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",

                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-scrollbarFiller": {
                          backgroundColor: theme.palette.background.alt,
                        },
                        "& .MuiDataGrid-menuIconButton": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-sortIcon": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                          flex: 1,
                        },
                        "& .MuiTypography-root.MuiTypography-body1": {
                          textAlign: "left !important",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiButton-root": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                          {
                            outline: "none !important",
                          },
                        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                          {
                            outline: "none !important",
                          },
                      }}
                      disableColumnMenu
                      disableColumnSorting
                    />
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel
                  value={tabValue}
                  index={3}
                  key="Tab Four"
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  <History
                    serviceHistories={linguistHistories}
                    entityType="linguist"
                  />
                </CustomTabPanel>
              </DragDropContext>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LinguistProfile;
